import type {
	ArtistAuthSessionEffect,
	ArtistAuthSessionEventKind,
	ArtistAuthSessionOutcome,
	ArtistAuthSessionProjection,
	ArtistAuthSessionReduceRequest,
	ArtistAuthSessionReduceResponse,
	ArtistAuthSessionState,
} from "@xy-devmode/artist-domain";
import Ajv2020, { type ValidateFunction } from "ajv/dist/2020.js";
import {
	KMP_ARTIFACT_API_VERSION,
	KMP_ARTIFACT_CONTRACT_DIGEST,
	KMP_ARTIFACT_CONTRACT_VERSION,
	KMP_ARTIFACT_PACKAGE_NAME,
} from "@/contracts/kmpArtifactContract";
import authSessionOperation from "../../../contracts/artist/operations/auth.session.reduce.v1.json";
import clearHintPayloadSchema from "../../../contracts/artist/schemas/auth-session-clear-hint-effect.schema.json";
import projectionSchema from "../../../contracts/artist/schemas/auth-session-projection.schema.json";
import reductionInputSchema from "../../../contracts/artist/schemas/auth-session-reduce-input.schema.json";
import stateSchema from "../../../contracts/artist/schemas/auth-session-state.schema.json";
import userEffectPayloadSchema from "../../../contracts/artist/schemas/auth-session-user-effect.schema.json";

export const ARTIST_AUTH_SESSION_DOMAIN_PACKAGE = KMP_ARTIFACT_PACKAGE_NAME;
export const ARTIST_AUTH_SESSION_ARTIFACT_API_VERSION =
	KMP_ARTIFACT_API_VERSION;
export const ARTIST_AUTH_SESSION_CONTRACT_VERSION =
	KMP_ARTIFACT_CONTRACT_VERSION;
export const ARTIST_AUTH_SESSION_CONTRACT_DIGEST = KMP_ARTIFACT_CONTRACT_DIGEST;

export type AuthSessionState = ArtistAuthSessionState;
export type AuthSessionReduceInput = ArtistAuthSessionReduceRequest["input"];
export type AuthSessionProjection = ArtistAuthSessionProjection;
export type AuthSessionOutcome = ArtistAuthSessionOutcome;
export type AuthSessionEffect = ArtistAuthSessionEffect;
export type AuthSessionReductionResult = ArtistAuthSessionReduceResponse;

export interface AuthSessionKernelDescriptor {
	readonly packageName: typeof ARTIST_AUTH_SESSION_DOMAIN_PACKAGE;
	readonly artifactApiVersion: typeof ARTIST_AUTH_SESSION_ARTIFACT_API_VERSION;
	readonly contractVersion: typeof ARTIST_AUTH_SESSION_CONTRACT_VERSION;
	readonly contractDigest: typeof ARTIST_AUTH_SESSION_CONTRACT_DIGEST;
}

export interface AuthSessionKernel {
	readonly descriptor: Readonly<AuthSessionKernelDescriptor>;
	reduce(
		initialState: AuthSessionState,
		input: AuthSessionReduceInput,
	): AuthSessionReductionResult;
}

export type AuthSessionKernelLoader = () => Promise<unknown>;

interface RawAuthSessionModule {
	describe(): unknown;
	authSessionReduce(requestJson: string): unknown;
}

const vendoredLoader: AuthSessionKernelLoader = () =>
	import("@xy-devmode/artist-domain");

const ajv = new Ajv2020({ allErrors: true, strict: true });
const validateState = ajv.compile<AuthSessionState>(stateSchema);
const validateReductionInput =
	ajv.compile<AuthSessionReduceInput>(reductionInputSchema);
const validateProjection = ajv.compile<AuthSessionProjection>(projectionSchema);
const validateUserEffectPayload = ajv.compile(userEffectPayloadSchema);
const validateClearHintPayload = ajv.compile(clearHintPayloadSchema);
const validateFailureOutcome = ajv.compile({
	$schema: "https://json-schema.org/draft/2020-12/schema",
	$id: "https://artist.genart/contracts/schemas/auth-session-failure-outcome.web.schema.json",
	type: "object",
	additionalProperties: false,
	required: ["kind", "error"],
	properties: {
		kind: { const: "failure" },
		error: {
			oneOf: authSessionOperation.typedErrors.map((error) => ({
				type: "object",
				additionalProperties: false,
				required: ["code", "category", "retryable", "messageKey", "context"],
				properties: {
					code: { const: error.code },
					category: { const: error.category },
					retryable: { const: error.retryable },
					messageKey: { const: error.messageKey },
					context: {
						type: "object",
						additionalProperties: false,
						required: ["eventKind"],
						properties: {
							eventKind: {
								enum: [
									"initial-session",
									"launch-timeout",
									"signed-in",
									"token-refreshed",
									"user-updated",
									"signed-out",
									"user-deleted",
								],
							},
						},
					},
				},
			})),
		},
	},
});

const effectKinds = [
	"cancel-user-scoped-work",
	"purge-user-private-cache",
	"clear-user-hint",
	"persist-user-hint",
	"start-post-login-sync",
] as const;
const allowedEffectSequences = new Set([
	"",
	"cancel-user-scoped-work|purge-user-private-cache|clear-user-hint",
	"cancel-user-scoped-work|purge-user-private-cache|persist-user-hint",
	"cancel-user-scoped-work|purge-user-private-cache|persist-user-hint|start-post-login-sync",
	"persist-user-hint",
	"persist-user-hint|start-post-login-sync",
	"start-post-login-sync",
]);

export class AuthSessionKernelUnavailableError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "AuthSessionKernelUnavailableError";
	}
}

export async function loadAuthSessionKernel(
	loader: AuthSessionKernelLoader = vendoredLoader,
): Promise<AuthSessionKernel> {
	try {
		const module = extractModule(await loader());
		const descriptor = normalizeDescriptor(module.describe());
		return {
			descriptor,
			reduce(initialState, input) {
				assertSchema(validateState, initialState, "auth session initial state");
				assertSchema(validateReductionInput, input, "auth session input");
				const rawResult = module.authSessionReduce(
					JSON.stringify({ initialState, input }),
				);
				if (typeof rawResult !== "string") {
					throw new TypeError(
						"Kotlin/JS auth session reducer must return a JSON string",
					);
				}
				return normalizeResult(JSON.parse(rawResult), initialState, input);
			},
		};
	} catch (error) {
		if (error instanceof AuthSessionKernelUnavailableError) throw error;
		throw new AuthSessionKernelUnavailableError(
			"The locked Kotlin/JS 0.8 Auth Session reducer is unavailable or invalid",
			{ cause: error },
		);
	}
}

function extractModule(value: unknown): RawAuthSessionModule {
	const direct = moduleShape(value);
	if (direct !== null) return direct;
	if (isRecord(value)) {
		const nested = moduleShape(value.default);
		if (nested !== null) return nested;
	}
	throw new AuthSessionKernelUnavailableError(
		"The package does not expose authSessionReduce",
	);
}

function moduleShape(value: unknown): RawAuthSessionModule | null {
	if (!isRecord(value)) return null;
	if (
		typeof value.describe !== "function" ||
		typeof value.authSessionReduce !== "function"
	) {
		return null;
	}
	return value as unknown as RawAuthSessionModule;
}

function normalizeDescriptor(value: unknown): AuthSessionKernelDescriptor {
	if (!isRecord(value)) throw new TypeError("descriptor must be an object");
	assertExactKeys(value, [
		"packageName",
		"artifactApiVersion",
		"contractVersion",
		"contractDigest",
	]);
	for (const [field, expected] of [
		["packageName", ARTIST_AUTH_SESSION_DOMAIN_PACKAGE],
		["artifactApiVersion", ARTIST_AUTH_SESSION_ARTIFACT_API_VERSION],
		["contractVersion", ARTIST_AUTH_SESSION_CONTRACT_VERSION],
		["contractDigest", ARTIST_AUTH_SESSION_CONTRACT_DIGEST],
	] as const) {
		if (value[field] !== expected) {
			throw new AuthSessionKernelUnavailableError(
				`Kotlin/JS descriptor ${field} does not match the locked Web boundary`,
			);
		}
	}
	return Object.freeze(value as unknown as AuthSessionKernelDescriptor);
}

function normalizeResult(
	value: unknown,
	initialState: AuthSessionState,
	input: AuthSessionReduceInput,
): AuthSessionReductionResult {
	if (!isRecord(value)) {
		throw new TypeError("auth session reduction result must be an object");
	}
	assertExactKeys(value, ["outcome", "finalState", "effects"]);
	const outcome = normalizeOutcome(value.outcome);
	assertSchema(validateState, value.finalState, "auth session final state");
	if (!Array.isArray(value.effects)) {
		throw new TypeError("auth session effects must be an array");
	}
	if (value.effects.length > effectKinds.length) {
		throw new TypeError("auth session effects exceed the contract maximum");
	}
	const seenKinds = new Set<string>();
	const effects = value.effects.map((effect, index) => {
		const normalized = normalizeEffect(effect, index);
		if (seenKinds.has(normalized.kind)) {
			throw new TypeError(
				`duplicate auth session effect kind: ${normalized.kind}`,
			);
		}
		seenKinds.add(normalized.kind);
		return normalized;
	});
	const effectSequence = effects.map((effect) => effect.kind).join("|");
	if (!allowedEffectSequences.has(effectSequence)) {
		throw new TypeError(
			`auth session effect sequence is invalid: ${effectSequence}`,
		);
	}
	assertMatchingEffectUsers(effects, [
		"cancel-user-scoped-work",
		"purge-user-private-cache",
	]);
	assertMatchingEffectUsers(effects, [
		"persist-user-hint",
		"start-post-login-sync",
	]);
	if (outcome.kind === "failure") {
		if (effects.length !== 0 || !jsonEqual(value.finalState, initialState)) {
			throw new TypeError(
				"auth session failure must preserve state and emit no effects",
			);
		}
		if (outcome.error.context.eventKind !== input.event.kind) {
			throw new TypeError(
				"auth session failure eventKind must match the input event",
			);
		}
	} else {
		const finalState = value.finalState as unknown as AuthSessionState;
		assertStructurallyCoherentState(finalState);
		const expectedProjection = projectAuthSession(
			finalState,
			input.nowEpochSeconds,
		);
		if (!jsonEqual(outcome.value, expectedProjection)) {
			throw new TypeError(
				"auth session success projection must exactly match finalState at the injected clock",
			);
		}
		assertAuthoritativeEffectUsers(effects, initialState, finalState, input);
	}
	return {
		outcome,
		finalState: value.finalState as unknown as AuthSessionState,
		effects,
	};
}

function assertStructurallyCoherentState(state: AuthSessionState): void {
	if (
		state.bootstrapPhase === "awaiting-initial" &&
		(state.userId !== null ||
			state.expiresAtEpochSeconds !== null ||
			state.postLoginPreparedForUserId !== null)
	) {
		throw new TypeError(
			"auth session successful finalState cannot attach a session or prepared scope while awaiting initial resolution",
		);
	}
	if (
		(state.userId === null) !== (state.expiresAtEpochSeconds === null) ||
		(state.postLoginPreparedForUserId !== null &&
			state.postLoginPreparedForUserId !== state.userId) ||
		(state.userId !== null &&
			state.userHintId !== null &&
			state.userHintId !== state.userId)
	) {
		throw new TypeError(
			"auth session successful finalState violates user, expiry, hint, or prepared-scope coherence",
		);
	}
}

function projectAuthSession(
	state: AuthSessionState,
	nowEpochSeconds: number,
): AuthSessionProjection {
	if (state.expiresAtEpochSeconds === null || state.userId === null) {
		return state.bootstrapPhase === "awaiting-initial"
			? {
					identity: "unknown",
					networkAccess: "unknown",
					sessionFreshness: "none",
					refreshRequired: false,
				}
			: {
					identity: "guest",
					networkAccess: "denied",
					sessionFreshness: "none",
					refreshRequired: false,
				};
	}
	const remainingSeconds = state.expiresAtEpochSeconds - nowEpochSeconds;
	if (remainingSeconds <= 0) {
		return {
			identity: "user",
			networkAccess: "denied",
			sessionFreshness: "expired",
			refreshRequired: true,
		};
	}
	if (remainingSeconds <= 300) {
		return {
			identity: "user",
			networkAccess: "granted",
			sessionFreshness: "expiring",
			refreshRequired: true,
		};
	}
	return {
		identity: "user",
		networkAccess: "granted",
		sessionFreshness: "fresh",
		refreshRequired: false,
	};
}

function assertAuthoritativeEffectUsers(
	effects: readonly AuthSessionEffect[],
	initialState: AuthSessionState,
	finalState: AuthSessionState,
	input: AuthSessionReduceInput,
): void {
	const oldScopeEffects = effects.filter(
		(effect) =>
			effect.kind === "cancel-user-scoped-work" ||
			effect.kind === "purge-user-private-cache",
	);
	if (oldScopeEffects.length > 0) {
		const oldScopeUserId = initialState.userId ?? initialState.userHintId;
		if (
			oldScopeUserId === null ||
			oldScopeEffects.some((effect) => effect.payload.userId !== oldScopeUserId)
		) {
			throw new TypeError(
				"auth session cancel/purge effect userId must match the authoritative initial user scope",
			);
		}
	}

	const newScopeEffects = effects.filter(
		(effect) =>
			effect.kind === "persist-user-hint" ||
			effect.kind === "start-post-login-sync",
	);
	if (newScopeEffects.length === 0) return;
	if (
		finalState.userId === null ||
		newScopeEffects.some(
			(effect) => effect.payload.userId !== finalState.userId,
		)
	) {
		throw new TypeError(
			"auth session persist/start effect userId must match successful finalState.userId",
		);
	}
	if (
		input.event.session === null ||
		newScopeEffects.some(
			(effect) => effect.payload.userId !== input.event.session?.userId,
		)
	) {
		throw new TypeError(
			"auth session persist/start effect userId must match input event.session.userId",
		);
	}
}

function normalizeOutcome(value: unknown): AuthSessionOutcome {
	if (!isRecord(value)) throw new TypeError("outcome must be an object");
	if (value.kind === "success") {
		assertExactKeys(value, ["kind", "value"]);
		assertSchema(validateProjection, value.value, "auth session projection");
		return value as unknown as AuthSessionOutcome;
	}
	if (value.kind === "failure") {
		assertSchema(validateFailureOutcome, value, "auth session failure outcome");
		return value as unknown as AuthSessionOutcome;
	}
	throw new TypeError("auth session outcome kind is invalid");
}

function normalizeEffect(value: unknown, index: number): AuthSessionEffect {
	if (!isRecord(value))
		throw new TypeError("auth session effect must be an object");
	assertExactKeys(value, [
		"effectId",
		"sequence",
		"logicalTimeMilliseconds",
		"kind",
		"payload",
		"idempotencyKey",
		"transactionId",
	]);
	if (
		value.effectId !== `auth.session.effect.${index}` ||
		value.sequence !== index ||
		value.logicalTimeMilliseconds !== 0 ||
		value.idempotencyKey !== null ||
		value.transactionId !== null ||
		!effectKinds.some((kind) => kind === value.kind)
	) {
		throw new TypeError("auth session effect metadata is invalid");
	}
	const payloadValidator =
		value.kind === "clear-user-hint"
			? validateClearHintPayload
			: validateUserEffectPayload;
	assertSchema(payloadValidator, value.payload, "auth session effect payload");
	return value as unknown as AuthSessionEffect;
}

function assertMatchingEffectUsers(
	effects: readonly AuthSessionEffect[],
	kinds: readonly AuthSessionEffect["kind"][],
): void {
	const userIds = effects
		.filter((effect) => kinds.includes(effect.kind))
		.map((effect) => effect.payload.userId);
	if (new Set(userIds).size > 1) {
		throw new TypeError(
			`auth session effect userId differs across ${kinds.join(" and ")}`,
		);
	}
}

function assertSchema(
	validator: ValidateFunction,
	value: unknown,
	label: string,
): void {
	if (!validator(value)) {
		throw new TypeError(
			`${label} violates the locked 0.8 schema: ${ajv.errorsText(validator.errors)}`,
		);
	}
}

function assertExactKeys(
	value: Readonly<Record<string, unknown>>,
	keys: readonly string[],
): void {
	const actual = Object.keys(value).toSorted();
	const expected = [...keys].toSorted();
	if (JSON.stringify(actual) !== JSON.stringify(expected)) {
		throw new TypeError(
			`object keys differ: expected ${expected.join(", ")}, received ${actual.join(", ")}`,
		);
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}

function jsonEqual(left: unknown, right: unknown): boolean {
	return (
		JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right))
	);
}

function canonicalize(value: unknown): unknown {
	if (Array.isArray(value)) return value.map((item) => canonicalize(item));
	if (isRecord(value)) {
		return Object.fromEntries(
			Object.entries(value)
				.toSorted(([left], [right]) => left.localeCompare(right, "en"))
				.map(([key, item]) => [key, canonicalize(item)]),
		);
	}
	return value;
}

export type AuthSessionEventKind = ArtistAuthSessionEventKind;
